import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  useTheme,
  Chip,
  Stack,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

/* ---------- helpers ---------- */
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
const safe = (v, def = "") => (v == null ? def : v);

// return a src suitable for <img>, from a file name or url-like string
const buildImageSrc = (entry) => {
  const val = typeof entry === "string" ? entry : entry?.name || "";
  if (!val) return null;
  // if already a usable URL / data / blob, use directly
  if (/^(https?:\/\/|data:|blob:)/i.test(val)) return val;

  // otherwise, treat it as a server-side filename
  const base = (
    process.env.REACT_APP_FILES_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/+$/, "");
  if (!base) return null;

  // common path for uploaded files on your server
  return `${base}/uploads/${encodeURIComponent(val)}`;
};

// very light check for image-ish extensions (for display fallback decisions)
const looksLikeImage = (nameOrUrl = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(nameOrUrl) ||
  /^(data:image\/|blob:)/i.test(nameOrUrl);

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // e.g. https://distributer.garamuletaapartment.com
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, { params });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewCar() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // table data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // dialogs
  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    carName: "",
    carPlate: "",
    carModel: "",
    driverName: "",
    driverPhone: "",
    carFiles: [],
  });

  // image preview srcs aligned to editValues.carFiles
  const [editPreviews, setEditPreviews] = React.useState([]);

  const [saving, setSaving] = React.useState(false);
  const [newFileLabel, setNewFileLabel] = React.useState("");
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  // track object URLs to revoke later
  const objectUrlsRef = React.useRef([]);

  React.useEffect(() => {
    return () => {
      // cleanup object URLs on unmount
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
    };
  }, []);

  // file picker (to add real files' names as labels)
  const fileInputRef = React.useRef(null);
  const handleClickPickFile = () => fileInputRef.current?.click();
  const handleFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // keep your original behavior (store the name)
      setEditValues((s) => ({
        ...s,
        carFiles: [...(s.carFiles || []), file.name],
      }));

      // add a local preview url for this picked file
      const url = URL.createObjectURL(file);
      objectUrlsRef.current.push(url);
      setEditPreviews((p) => [...p, url]);
    }
    // reset input so the same file can be picked again later if needed
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addFileLabel = () => {
    const v = newFileLabel.trim();
    if (!v) return;
    setEditValues((s) => ({ ...s, carFiles: [...(s.carFiles || []), v] }));
    // build a preview src if it looks like an image or filename
    const candidate = buildImageSrc(v);
    setEditPreviews((p) => [...p, candidate || ""]);
    setNewFileLabel("");
  };
  const removeFileLabel = (idx) => {
    setEditValues((s) => ({
      ...s,
      carFiles: (s.carFiles || []).filter((_, i) => i !== idx),
    }));
    setEditPreviews((p) => p.filter((_, i) => i !== idx));
  };

  // fetch list (cache-busted)
  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await getJSON("/api/carInfo", { _: Date.now() });
    setLoading(false);
    if (error) {
      setToast({ open: true, msg: "Failed to load cars", sev: "error" });
      return;
    }
    setRows(Array.isArray(data) ? data : []);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // columns to display in table
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        // index among visible rows on the current page
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        // page & pageSize for absolute numbering across pages
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "id", headerName: "ID", width: 90, hide: true },
    { field: "carName", headerName: "Car Name", flex: 1, minWidth: 160 },
    { field: "carPlate", headerName: "Plate", width: 140 },
    { field: "carModel", headerName: "Model", width: 120 },
    { field: "driverName", headerName: "Driver", flex: 1, minWidth: 160 },
    { field: "driverPhone", headerName: "Phone", width: 140 },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  setEditRow(row);
                  setEditValues({
                    carName: safe(row.carName),
                    carPlate: safe(row.carPlate),
                    carModel: safe(row.carModel),
                    driverName: safe(row.driverName),
                    driverPhone: safe(row.driverPhone),
                    carFiles: Array.isArray(row.carFiles)
                      ? [...row.carFiles]
                      : [],
                  });

                  // initialize previews from existing values
                  const existing = Array.isArray(row.carFiles)
                    ? row.carFiles
                    : [];
                  setEditPreviews(existing.map((v) => buildImageSrc(v) || ""));

                  setNewFileLabel("");
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // grid look (theme-consistent)
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  // update (optimistic + refetch)
  const handleUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);

    // Send a single string for carFiles (last item in the list), per your requirement
    const singleFile =
      Array.isArray(editValues.carFiles) && editValues.carFiles.length > 0
        ? editValues.carFiles[editValues.carFiles.length - 1]
        : "";

    const payload = {
      carName: String(editValues.carName ?? "").trim(),
      carPlate: String(editValues.carPlate ?? "").trim(),
      carModel: String(editValues.carModel ?? "").trim(),
      carFiles: Array.isArray(editValues.carFiles)
        ? editValues.carFiles
        : singleFile
        ? [singleFile]
        : [],

      driverName: String(editValues.driverName ?? "").trim(),
      driverPhone: String(editValues.driverPhone ?? "").trim(),
    };

    const { data, error } = await putJSON(
      `/api/carInfo/${editRow.id}`,
      payload
    );
    setSaving(false);

    if (error) {
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    // Merge returned object (or fallback to payload)
    const updated = data?.data || data;
    const merged = updated?.id ? updated : { id: editRow.id, ...payload };

    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? { ...r, ...merged } : r))
    );

    setToast({ open: true, msg: "Car updated successfully", sev: "success" });
    setEditOpen(false);
    setEditRow(null);

    // refresh for server timestamps/consistency
    load();
  };

  // delete (optimistic + refetch)
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const { data, error } = await deleteJSON(`/api/carInfo/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));

    setToast({
      open: true,
      msg: data?.message || "Car deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);

    load();
  };

  return (
    <>
      <Box m="20px">
        <Header title="CARS" subtitle="View, edit, and manage cars" />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* Details */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Car Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Car Name" value={detailRow.carName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Plate" value={detailRow.carPlate} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue label="Model" value={detailRow.carModel} />
              </Grid>
              <Grid item xs={12}>
                <LabelValue label="Driver Name" value={detailRow.driverName} />
              </Grid>
              <Grid item xs={12}>
                <LabelValue
                  label="Driver Phone"
                  value={detailRow.driverPhone}
                />
              </Grid>

              {/* IMAGES PREVIEW (Details) */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Files
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {Array.isArray(detailRow.carFiles) &&
                  detailRow.carFiles.length > 0 ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(120px, 1fr))",
                        gap: 1,
                      }}
                    >
                      {detailRow.carFiles.map((f, i) => {
                        const label = String(f?.name || f || `File ${i + 1}`);
                        const src = buildImageSrc(f);
                        const isPdf = label.toLowerCase().endsWith(".pdf");
                        const isImage =
                          looksLikeImage(label) ||
                          /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(label);

                        return (
                          <Box
                            key={i}
                            sx={{
                              position: "relative",
                              borderRadius: 1,
                              overflow: "hidden",
                              border: "1px solid",
                              borderColor: "divider",
                              height: 100,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "background.paper",
                            }}
                          >
                            {isPdf ? (
                              <a
                                href={src}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textDecoration: "none",
                                  color: "#d32f2f",
                                  fontWeight: 600,
                                  fontSize: 14,
                                  width: "100%",
                                  height: "100%",
                                  background: "#f5f5f5",
                                }}
                              >
                                PDF
                              </a>
                            ) : isImage ? (
                              <img
                                src={src}
                                alt={label}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Chip
                                label={label}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Chip label="No files" size="small" variant="outlined" />
                  )}
                </Box>
                <Divider sx={{ mt: 1 }} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Car</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="Car Name"
              value={editValues.carName}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, carName: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="Plate"
              value={editValues.carPlate}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, carPlate: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="Model"
              value={editValues.carModel}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, carModel: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="Driver Name"
              value={editValues.driverName}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, driverName: e.target.value }))
              }
              fullWidth
              variant="filled"
            />
            <TextField
              label="Driver Phone"
              value={editValues.driverPhone}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, driverPhone: e.target.value }))
              }
              fullWidth
              variant="filled"
            />

            {/* Car Files (image/pdf grid with X to remove) */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Car Files
            </Typography>

            {!Array.isArray(editValues.carFiles) ||
            editValues.carFiles.length === 0 ? (
              <Chip label="No files" variant="outlined" size="small" />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 1,
                }}
              >
                {editValues.carFiles.map((f, i) => {
                  const label = String(f?.name || f || `File ${i + 1}`);
                  const src = buildImageSrc(f); // keep your existing helper
                  const isPdf = /\.pdf$/i.test(label);
                  const isImage =
                    looksLikeImage?.(label) ||
                    /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(label);

                  return (
                    <Box
                      key={i}
                      sx={{
                        position: "relative",
                        borderRadius: 1,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "background.paper",
                      }}
                    >
                      {isPdf ? (
                        <a
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textDecoration: "none",
                            color: "#d32f2f",
                            fontWeight: 600,
                            fontSize: 14,
                            width: "100%",
                            height: "100%",
                            background: "#f5f5f5",
                          }}
                          title={label}
                        >
                          PDF
                        </a>
                      ) : isImage && src ? (
                        <img
                          src={src}
                          alt={label}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Chip label={label} size="small" variant="outlined" />
                      )}

                      {/* X button to remove */}
                      <IconButton
                        size="small"
                        onClick={() => removeFileLabel(i)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                        aria-label={`Remove ${label}`}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Add file: + icon triggers file picker; text input allows manual label */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" component="label" color="neutral">
                Choose File
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={handleFileChosen}
                />
              </Button>

              {editValues.carFiles?.length > 0 && (
                <Typography variant="body2" color="text.primary">
                  {editValues.carFiles[editValues.carFiles.length - 1]}
                </Typography>
              )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Car</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
        
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
